import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getArticlePrice } from '@/lib/stripe';

// Get article for checkout by slug and language
export function useArticleForCheckout(slug: string, lang: string = 'fr') {
  return useQuery({
    queryKey: ['article-checkout', slug, lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .eq('lang', lang)
        .eq('status', 'published')
        .eq('is_premium', true)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Article not found');
      return data;
    },
    enabled: !!slug,
  });
}

// Get user's purchase count to calculate dynamic pricing
export function usePurchaseCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['purchase-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from('article_purchases')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
  });
}

// Validate promo code
export function useValidatePromo() {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (promoCode: string) => {
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: {
          code: promoCode,
          userId: user?.id,
          targetType: 'articles',
        },
      });

      if (error || !data?.valid) {
        throw new Error(data?.error || 'Code promo invalide');
      }

      return {
        discount: data.promoCode.discountValue,
        type: data.promoCode.discountType as 'percent' | 'amount',
        id: data.promoCode.id,
        code: data.promoCode.code,
      };
    },
  });
}

// Create Stripe Payment Intent
export function useCreatePaymentIntent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      articleId,
      amount,
      promoCode,
      promoCodeId,
      guestEmail,
      guestFirstName,
      guestLastName,
      billingCompany,
      billingAddress,
      billingPostalCode,
      billingCity,
      billingCountry,
      billingSiret,
      billingVatNumber,
    }: {
      articleId: string;
      amount: number;
      promoCode?: string;
      promoCodeId?: string;
      guestEmail?: string;
      guestFirstName?: string;
      guestLastName?: string;
      billingCompany?: string;
      billingAddress?: string;
      billingPostalCode?: string;
      billingCity?: string;
      billingCountry?: string;
      billingSiret?: string;
      billingVatNumber?: string;
    }) => {
      // Guest checkout allowed - user can be null
      if (!user && (!guestEmail || !guestFirstName || !guestLastName)) {
        throw new Error('Email, prénom et nom requis pour l\'achat');
      }

      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          articleId,
          amount,
          promoCode,
          promoCodeId,
          // Pass guest details if not authenticated
          guestEmail: !user ? guestEmail : undefined,
          guestFirstName: !user ? guestFirstName : undefined,
          guestLastName: !user ? guestLastName : undefined,
          // Informations de facturation
          billingCompany,
          billingAddress,
          billingPostalCode,
          billingCity,
          billingCountry,
          billingSiret,
          billingVatNumber,
        },
      });

      if (error) {
        throw new Error(error.message || 'Erreur lors de la création du paiement');
      }

      if (!data?.clientSecret) {
        throw new Error('Pas de client secret retourné');
      }

      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
      };
    },
  });
}

// Calculate final price with promo code
export function calculateFinalPrice(
  basePrice: number,
  promo?: { discount: number; type: 'percent' | 'amount' }
): number {
  if (!promo) return basePrice;

  if (promo.type === 'percent') {
    return Math.round(basePrice * (1 - promo.discount / 100));
  } else {
    return Math.max(0, basePrice - promo.discount);
  }
}
